import { Box, Button, IconButton, ListItem, ListItemText } from '@mui/material'
import {
  ConfirmationDialog,
  DeleteIcon,
  EditIcon,
  SectionHeader,
} from '@sitoo/mui-components'
import {
  UseControllerProps,
  FieldValues,
  Path,
  useFieldArray,
  Control,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Consent } from '../../../../generated/graphql'
import { useState } from 'react'
import { ConsentDialog } from './consent-dialog'

export const ConsentsInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(
  props: UseControllerProps<TFieldValues, TName>,
) => {
  const { t } = useTranslation(['apps', 'shared'])
  const { control } = props

  const [dialogData, setDialogData] = useState<Consent | null>(null)
  const [confirmDialogIndex, setConfirmDialogIndex] = useState<number | null>(
    null,
  )

  const { fields, update, append, remove } = useFieldArray({
    control: control as
      | Control<{ consents: Consent[] }, any, { consents: Consent[] }>
      | undefined,
    name: 'consents',
  })

  return (
    <>
      <SectionHeader variant="transparent">
        {t('apps:recognition:consents_label')}
      </SectionHeader>

      {fields?.map((consent, index, items) => (
        <ListItem
          key={consent.id}
          divider={index < items.length - 1}
          component="div"
          secondaryAction={
            <>
              <IconButton onClick={() => setDialogData(consent)}>
                <EditIcon />
              </IconButton>

              <IconButton onClick={() => setConfirmDialogIndex(index)}>
                <DeleteIcon />
              </IconButton>
            </>
          }
        >
          <ListItemText primary={consent.name} />
        </ListItem>
      ))}

      <Box sx={{ p: 2 }}>
        <Button
          color="secondary"
          fullWidth
          onClick={() => {
            setDialogData({
              consent_id: '',
              name: '',
              description: '',
              required: false,
            })
          }}
        >
          {t('apps:recognition:consent_add_label')}
        </Button>
      </Box>

      <ConsentDialog
        open={!!dialogData}
        data={dialogData}
        onClose={() => setDialogData(null)}
        onSave={(formData: Consent & { id: string }) => {
          const fieldIndex = fields.findIndex((f) => f.id === formData.id)
          const { id, ...data } = formData
          if (fieldIndex === -1) {
            append(data)
          } else {
            update(fieldIndex, data)
          }
        }}
      />

      <ConfirmationDialog
        open={confirmDialogIndex !== null}
        title={t('apps:recognition.delete_dialog.title', {
          name: fields[confirmDialogIndex!]?.name || '',
        })}
        text={t('apps:recognition.delete_dialog.description')}
        variant="destructive"
        confirmAction={() => {
          if (confirmDialogIndex !== null) remove(confirmDialogIndex)
          setConfirmDialogIndex(null)
        }}
        onClose={() => setConfirmDialogIndex(null)}
      />
    </>
  )
}
